'use client';

import { usePathname, useRouter } from '@/src/i18n/navigation';
import { Search, X } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import React, { useTransition, useRef, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Spinner from '../ui/Spinner';

interface SearchQueryProps {
  queryName: string;
  placeholder?: string;
  trackEvent?: boolean;
  event?: any;
  className?: string;
}

const SearchQuery: React.FC<SearchQueryProps> = ({
  queryName,
  placeholder,
  trackEvent,
  event,
  className,
}) => {
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();
  const { replace } = useRouter();
  const searchValue = searchParams.get(queryName)?.toString();

  // Use ref to directly manipulate input without causing re-renders
  const inputRef = useRef<HTMLInputElement>(null);

  const updateSearchParams = useCallback(
    (params: URLSearchParams) => {
      replace(`${pathname}?${params.toString()}`);
    },
    [pathname, replace],
  );

  const handleSearch = useDebouncedCallback((term: string) => {
    startTransition(() => {
      const params = new URLSearchParams(searchParams);
      term ? params.set(queryName, term) : params.delete(queryName);
      updateSearchParams(params);
    });
  }, 350);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
    if (trackEvent && event && e.target.value) {
      event();
    }
  };

  const handleClearSearch = () => {
    // Clear input directly via ref
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    startTransition(() => {
      const params = new URLSearchParams(searchParams);
      params.delete(queryName);
      updateSearchParams(params);
    });
  };

  return (
    <div className="w-full">
      <div className="relative">
        <div
          className={`trl:right-0 absolute inset-y-0 flex cursor-pointer items-center ltr:left-0 ltr:pl-3 rtl:pr-3 ${className}`}
        >
          <Search className="h-5 w-5" />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder ?? 'Search something here'}
          defaultValue={searchValue ?? ''}
          onChange={handleInputChange}
          suppressHydrationWarning
          className="placeholder:text-text/50 focus:ring-primary w-full rounded-full border px-10 py-1.5 shadow-xs placeholder:font-medium focus:ring-2 focus:outline-hidden"
        />
        <div className="absolute inset-y-0 flex cursor-pointer items-center ltr:right-0 ltr:pr-3 rtl:left-0 rtl:pl-3">
          {searchValue && !isPending && (
            <span className="trl:right-0 ltr:left-0" onClick={handleClearSearch}>
              <X size={18} />
            </span>
          )}
          {isPending && <Spinner size="small" />}
        </div>
      </div>
    </div>
  );
};

export default SearchQuery;
